import type { ReactNode } from 'react';

import { Communication, General } from '../StrokeIcons';
import type { ColorName, Style } from './buttonClasses';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import FilterSelect from './FilterSelect';
import type { Size } from './types';

const responseOptions = [
  { value: 'ever-responded', label: 'Lead responded', icon: <General.AtSign /> },
  { value: 'never-responded', label: 'Lead not responded', icon: <General.AtSign /> },
  // "Reached" = the campaign actually sent the lead outreach (the denominator behind the
  // Replies card's "total outreach"). Lets the denominator click render a labelled filter chip.
  { value: 'reached', label: 'Reached', icon: <Communication.Send01 /> },
] as const;

export type ResponseFilterSelectProps = {
  value?: string;
  /** The default/reset value. When provided and value !== defaultValue, shows X mark to clear. */
  defaultValue?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: Size;
  disabled?: boolean;
  /** Show the selected label/placeholder text. Default true. */
  showText?: boolean;
  /** Show the dropdown chevron. Default true. */
  showChevron?: boolean;
  bordered?: boolean;
  shape?: 'rounded' | 'pill' | 'square';
  color?: ColorName;
  tone?: Style;
  tooltip?: ReactNode;
  /** Custom clear handler. When provided with defaultValue, called instead of onChange(defaultValue). */
  onClear?: () => void;
};

export default function ResponseFilterSelect({
  value,
  defaultValue,
  onChange,
  placeholder = 'Campaign activity',
  className,
  size,
  disabled,
  showText,
  showChevron,
  bordered,
  shape,
  color,
  tone,
  tooltip,
  onClear,
}: ResponseFilterSelectProps) {
  const showClearButton = defaultValue !== undefined && value !== defaultValue;

  const select = (
    <FilterSelect
      icon={<General.AtSign />}
      options={[...responseOptions]}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      autoWidth
      size={size}
      showText={showText}
      showChevron={showClearButton ? false : showChevron}
      bordered={bordered}
      shape={shape}
      color={color}
      tone={tone}
      tooltip={tooltip}
    />
  );

  if (showClearButton) {
    return (
      <ButtonGroup size={size}>
        {select}
        <Button icon={<General.XMark />} onClick={onClear ?? (() => onChange(defaultValue))} />
      </ButtonGroup>
    );
  }

  return select;
}
